import React, { useState, useContext } from 'react';
import { useAllOrders } from '../context/AllOrdersContext';
import { useAuth } from '../context/AuthContext';
import { AppContext } from '../context/AppContext';
import { OrderIcon } from '../components/icons';
import DriverRatingModal from '../components/DriverRatingModal';
import LiveTrackingMap from '../components/LiveTrackingMap';
import LiveTrackingModal from '../components/LiveTrackingModal';

import { useData } from '../context/DataContext';
import { parseDate, formatDateReadable } from '../utils/dateUtils';


const OrderScreen: React.FC = () => {
  const { placedOrders, loading, cancelOrder, updateOrderStatus, nudgeRestaurant, loadMoreOrders, hasMore, loadingMore } = useAllOrders();
  const { restaurants } = useData();
  const { currentUser } = useAuth();
  const { setView } = useContext(AppContext);

  const [activeTab, setActiveTab] = useState<'sent' | 'completed' | 'cancelled'>('sent');
  const [ratingOrder, setRatingOrder] = useState<any | null>(null);
  const [trackingOrder, setTrackingOrder] = useState<any | null>(null);
  const [, setTick] = useState(0);

  // Force re-render every 30s to update countdowns
  React.useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 30000);
    
    // Also refresh when app becomes visible again (common PWA issue on Android)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTick(t => t + 1);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const getRemainingPrepTime = (order: any) => {
    if (!order.preparationTime || !order.preparationAt) return order.preparationTime;
    
    try {
      const prepAtDate = parseDate(order.preparationAt);
        
      if (isNaN(prepAtDate.getTime())) return order.preparationTime;

      const elapsedMinutes = (Date.now() - prepAtDate.getTime()) / 60000;
      // Allow it to go to 0 or negative for the nudge logic
      return Math.round(order.preparationTime - elapsedMinutes);
    } catch (e) {
      console.error("Error calculating prep time:", e);
      return order.preparationTime;
    }
  };

  const getCanNudge = (order: any) => {
    if (order.status !== 'En préparation') return false;
    const remaining = getRemainingPrepTime(order);
    if (remaining > 0) return false;
    
    if (order.lastNudgeAt) {
      const lastNudge = new Date(order.lastNudgeAt);
      const diffMs = Date.now() - lastNudge.getTime();
      return diffMs > 5 * 60000; // 5 min cooldown
    }
    
    return true;
  };

  // Filter orders based on status
  // Improvement: Keep 'Terminée' in 'En cours' if not yet rated, so it's visible to the user for confirmation/rating
  const historySent = placedOrders.filter(o => 
    (['En attente', 'En préparation', 'Envoyée', 'Prête', 'Livrée'].includes(o.status) && o.restaurantRating === undefined) || 
    (o.status === 'Terminée' && o.restaurantRating === undefined)
  );

  const historyCompleted = placedOrders.filter(o => o.status === 'Terminée' && o.restaurantRating !== undefined);
  const historyCancelled = placedOrders.filter(o => ['Annulée', 'Refusée'].includes(o.status));

  const renderOrderList = (orders: any[]) => {
    if (orders.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-center px-6">
          <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
            <OrderIcon className="w-10 h-10 text-gray-300" />
          </div>
          <p className="text-gray-500 font-bold">Aucune commande trouvée.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {orders.map(order => {
          const isRegisteredDriver = order.livreurId && !order.livreurId.startsWith('manual_');
          
          return (
            <div key={order.id} className="bg-white p-7 rounded-[2.5rem] shadow-[0_15px_35px_-10px_rgba(0,0,0,0.1)] border border-gray-100 hover:shadow-[0_20px_45px_-12px_rgba(0,0,0,0.15)] transition-all duration-500 group relative overflow-hidden">
            <div className="flex justify-between items-start mb-5 relative z-10">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1.5">
                  <span className={`w-3.5 h-3.5 rounded-full ring-4 ring-offset-2 ${['En attente', 'En préparation'].includes(order.status) ? 'bg-yellow-400 animate-pulse ring-yellow-400/20' :
                    ['Envoyée', 'Prête', 'Livrée'].includes(order.status) ? 'bg-blue-500 ring-blue-500/20' :
                      order.status === 'Terminée' ? 'bg-green-500 ring-green-500/20' : 'bg-gray-400 ring-gray-400/20'
                    }`} />
                  <h3 className="font-black text-gray-900 text-xl uppercase tracking-tighter italic group-hover:text-red-600 transition-colors">
                    {order.restaurantName || order.items?.[0]?.restaurantName || 'Restaurant'}
                  </h3>
                </div>
                <div className="flex flex-col gap-1.5">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] flex items-center gap-2 italic">
                    <span className="opacity-50">#</span> {formatDateReadable(order.timestamp)}
                  </p>
                  {order.customerAddress && (
                    <p className="text-[10px] text-gray-600 font-black flex items-center gap-2 mt-1 bg-gray-50/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-gray-100 w-fit uppercase tracking-wider">
                      <span className="text-xs grayscale">📍</span> {order.customerAddress}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className={`px-4 py-2 text-[10px] font-black uppercase rounded-full shadow-sm tracking-widest italic border ${
                  order.status === 'En attente' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                  order.status === 'En préparation' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  order.status === 'Envoyée' ? 'bg-green-50 text-green-700 border-green-200' :
                  order.status === 'Livrée' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                  order.status === 'Terminée' ? 'bg-red-600 text-white border-red-600' : 'bg-gray-100 text-gray-700 border-gray-200'
                }`}>
                  {order.status}
                </span>
                <span className="px-3 py-1 bg-red-50 text-red-600 text-[9px] font-black uppercase rounded-full border border-red-100 shadow-sm tracking-wider italic">
                  {order.deliveryType === 'takeaway' ? 'À emporter' : 'Livraison'}
                </span>
              </div>
            </div>

            {(order.status === 'En préparation' || (['Envoyée', 'Prête'].includes(order.status) && order.estimatedDeliveryTime)) && (
              <div className="mb-4 bg-red-600 text-white rounded-[2.5rem] p-7 shadow-2xl border border-white/10 relative overflow-hidden group/track">
                {/* Visual Accents */}
                <div className="absolute -right-20 -top-20 w-56 h-56 bg-red-600/10 rounded-full blur-3xl animate-pulse" />
                <div className="absolute -left-20 -bottom-20 w-56 h-56 bg-blue-600/10 rounded-full blur-3xl" />
                <div className={`grid ${order.deliveryType === 'takeaway' ? 'grid-cols-1' : 'grid-cols-2 divide-x'} gap-4 relative z-10 divide-white/10`}>
                  <div className="pr-4">
                    <p className="text-[10px] font-black text-red-100 uppercase tracking-[0.2em] mb-4 italic flex items-center justify-center gap-2">
                       PRÉPARATION
                    </p>
                    {order.status === 'En préparation' && order.preparationTime ? (
                      <div className="space-y-2">
                        <p className={`text-4xl font-black italic tracking-tighter flex items-center justify-center gap-1.5 ${getRemainingPrepTime(order) <= 0 ? 'text-red-300 animate-pulse' : 'text-white'}`}>
                          {Math.max(0, getRemainingPrepTime(order))}
                          <span className="text-[10px] text-red-200 uppercase tracking-widest font-black italic">min</span>
                        </p>
                        <span className="text-[9px] bg-white/10 text-white px-2.5 py-1 rounded-full font-black uppercase tracking-[0.2em] inline-block border border-white/20 italic">Live Chrono</span>
                      </div>
                    ) : (['Envoyée', 'Prête', 'Livrée', 'Terminée'].includes(order.status)) ? (
                      <div className="opacity-50">
                        <p className="text-lg font-black text-green-300 flex items-center gap-2">
                          ✓ <span className="text-xs">PRÊT</span>
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs font-black text-red-100 uppercase italic">En attente...</p>
                    )}
                  </div>

                  {order.deliveryType !== 'takeaway' && (
                    <div className="pl-6">
                      <p className="text-[10px] font-black text-red-100 uppercase tracking-widest mb-2 flex items-center justify-center gap-2">
                        LIVRAISON
                      </p>
                      {order.estimatedDeliveryTime ? (
                        <div>
                          <p className="text-2xl font-black text-white flex items-baseline justify-center gap-1">
                            {order.estimatedDeliveryTime}
                            <span className="text-[10px] text-red-200 uppercase tracking-tighter font-bold">min</span>
                          </p>
                          <p className="text-[8px] text-red-100 font-bold uppercase tracking-wider mt-1 opacity-70 text-center">
                            {order.status === 'Envoyée' ? 'En mouvement' : 'ESTIMATION'}
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center pt-2">
                          <p className="text-sm font-black text-white uppercase italic leading-none">En attente...</p>
                          <p className="text-[10px] text-red-200 font-bold mt-2 uppercase tracking-widest">Attente Livreur</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Nudge Feature */}
                {order.status === 'En préparation' && (
                  <div className="mt-4 pt-4 border-t border-white/10 relative z-20">
                      {order.nudgeResponse ? (
                        <div className={`p-3 rounded-xl border flex items-center gap-3 ${
                          order.nudgeResponse === 'coming' 
                            ? 'bg-white/10 border-white/20 text-white' 
                            : 'bg-amber-500/10 border-amber-500/20 text-amber-400'
                        }`}>
                          <div className="flex-1 text-center">
                            <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1">Réponse du restaurant</p>
                            <p className="text-xs font-bold leading-relaxed">
                              {order.nudgeResponse === 'coming' ? (<span>“Ça arrive, on finit votre <span className="whitespace-nowrap">commande&nbsp;!”</span></span>) : (<span>“Désolé, on a besoin de quelques minutes de plus.”</span>)}
                            </p>
                          </div>
                        </div>
                      ) : getCanNudge(order) ? (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          nudgeRestaurant(order.id);
                        }}
                        className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-red-900/40 transition-all active:scale-95 flex items-center justify-center gap-2"
                      >
                        🔔 Relancer le restaurant
                      </button>
                    ) : order.nudgeCount > 0 ? (
                      <div className="text-center">
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest italic group">
                          Relance envoyée ({order.nudgeCount}) • Prochaine possible dans 5 min
                        </p>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            <div className="mt-2 flex items-center justify-end">
              <div className="flex items-center gap-2">
                {order.status === 'Terminée' && order.restaurantRating === undefined && (
                  <button
                    onClick={() => setRatingOrder(order)}
                    className="px-7 py-3.5 bg-red-600 text-white font-black rounded-[1.5rem] text-[10px] uppercase italic tracking-[0.15em] shadow-xl shadow-red-100 hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
                  >
                    <span>⭐</span> Noter la commande
                  </button>
                )}
              </div>
            </div>
            <div className="mt-2 bg-gray-50/50 rounded-2xl p-5 border border-gray-100/50">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4 italic">Contenu de la commande</p>
              {order.items?.map((item: any, idx: number) => {
                const isPack = item.type === 'pack';
                const itemName = isPack ? (item.pack?.name || 'Pack') : (item.dish?.name || 'Plat');
                const itemPrice = isPack ? (item.pack?.packPrice || 0) : (item.dish?.price || 0);

                return (
                  <div key={idx} className="py-2.5 border-b border-gray-100/50 last:border-0">
                    <div className="flex justify-between items-baseline">
                      <p className="text-sm font-black text-gray-900 uppercase tracking-tighter italic">
                        <span className="text-red-600 mr-2">{item.quantity}x</span> {itemName}
                      </p>
                      <span className="text-xs font-black text-gray-400 italic">{itemPrice * item.quantity} DA</span>
                    </div>
                    {item.selectedExtras && item.selectedExtras.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {item.selectedExtras.map((extra: any) => (
                          <span key={extra.id} className="text-[9px] bg-white text-gray-500 px-1.5 py-0.5 rounded border border-gray-100 font-bold uppercase italic tracking-tight">
                            + {extra.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Driver Info if Sent or Delivered */}
            {['Envoyée', 'Livrée'].includes(order.status) && order.assignedDriver && (
              <div className="bg-green-50 p-4 rounded-2xl flex items-center justify-between mb-4 border border-green-100 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-green-200/20 rounded-full -mr-8 -mt-8" />
                <div className="flex items-center relative z-10">
                  <div className="bg-green-200 p-2 rounded-xl mr-3 shadow-inner">
                    <span className="text-xl">🛵</span>
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-green-800 uppercase tracking-widest leading-none mb-1">Votre Livreur</p>
                    <p className="text-sm font-black text-gray-800">{order.assignedDriver.name}</p>
                  </div>
                </div>
                <div className="flex gap-2 relative z-10">
                  {order.assignedDriver.phone && (
                    <a href={`tel:${order.assignedDriver.phone}`} className="bg-white p-2.5 rounded-xl shadow-sm text-green-600 hover:bg-green-50 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    </a>
                  )}
                  {['En préparation', 'Prête', 'Envoyée'].includes(order.status) && 
                   ((order.livreurStatus === 'accepted') || (order.status === 'Envoyée' && order.livreurStatus !== 'rejected')) && 
                   isRegisteredDriver && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setTrackingOrder(order);
                      }} 
                      className="p-2.5 bg-green-600 text-white rounded-xl shadow-lg shadow-green-100 flex items-center gap-1.5 active:scale-95 transition-all"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-[10px] font-black uppercase tracking-widest">Suivre</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {order.comment && (
              <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <p className="text-[9px] font-black text-amber-800 uppercase tracking-widest mb-1 italic">Note pour le restaurant :</p>
                <p className="text-xs text-amber-900 font-bold leading-relaxed">"{order.comment}"</p>
              </div>
            )}

            <div className="flex justify-between items-center pt-4 border-t border-gray-100">
              <div>
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">Total Payé</p>
                <p className="font-black text-red-600 text-2xl leading-none">{order.totalPrice} DA</p>
                {order.deliveryFee > 0 ? (
                    <p className="text-[9px] text-gray-400 font-bold tracking-tighter uppercase mt-1">Dont {order.deliveryFee} DA livraison</p>
                ) : (
                    <p className="text-[9px] text-green-500 font-bold tracking-tighter uppercase mt-1">Livraison gratuite</p>
                )}
              </div>

              {order.status === 'En attente' ? (
                <div className="flex flex-col items-end gap-2 ml-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setView({ screen: 'restaurantDetail', params: { id: order.restaurantId, editOrderId: order.id } });
                    }}
                    className="px-6 py-2.5 bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-blue-100 transition-colors w-full sm:w-auto text-center"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Annuler cette commande ?')) cancelOrder(order.id);
                    }}
                    className="px-6 py-2 bg-red-50 text-red-600 text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-red-100 transition-colors w-full sm:w-auto text-center"
                  >
                    Annuler
                  </button>
                </div>
              ) : (order.status === 'Livrée' && isRegisteredDriver) ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setRatingOrder(order);
                  }}
                  className="px-8 py-3 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 flex items-center gap-2 transform active:scale-95"
                >
                  <span>⭐</span> Confirmer & Noter
                </button>
              ) : ((order.status === 'Envoyée' && !isRegisteredDriver && order.assignedDriver) || (order.deliveryType === 'takeaway' && order.status === 'Prête')) ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setRatingOrder(order);
                  }}
                  className="px-8 py-3 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 flex items-center gap-2 transform active:scale-95"
                >
                  <span>✅</span> Reçu
                </button>
              ) : (order.status === 'Terminée' && !order.restaurantRating) ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setRatingOrder(order);
                  }}
                  className="px-8 py-3 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 flex items-center gap-2 transform active:scale-95"
                >
                  <span>⭐</span> Noter le restaurant
                </button>
              ) : (['Annulée', 'Refusée'].includes(order.status)) ? (
                <div className="flex flex-col items-end">
                   <p className="text-[10px] text-gray-400 font-bold italic">
                    {formatDateReadable(order.timestamp)}
                  </p>
                </div>
              ) : (order.status === 'Envoyée' && isRegisteredDriver) ? (
                <div className="flex flex-col items-end">
                  <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest animate-pulse">Livreur en route... 🛵</span>
                  <p className="text-[9px] text-gray-400 font-bold mt-1">Vérifiez la carte pour suivre</p>
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 font-bold italic">
                  {formatDateReadable(order.timestamp)}
                </p>
              )}
            </div>
          </div>
        )})}
      </div>
    );
  };

  return (
    <div className="px-6 py-10 pb-32 max-w-lg mx-auto bg-gray-50/30 min-h-screen">
      <div className="mb-10">
        <h1 className="text-4xl font-black text-gray-900 tracking-tighter uppercase italic">Commandes</h1>
        <p className="text-gray-400 font-bold text-sm mt-1">Retrouvez l'historique de vos festins</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 p-1 bg-white rounded-2xl shadow-sm border border-gray-100 mb-8 overflow-x-auto no-scrollbar">
        {[
          { id: 'sent', label: 'En cours' },
          { id: 'completed', label: 'Terminé' },
          { id: 'cancelled', label: 'Annulé' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 py-3 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-red-600 text-white shadow-lg shadow-red-100' : 'text-gray-400 hover:bg-gray-50'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-400 font-bold">Chargement de vos commandes...</p>
        </div>
      ) : (
        <div className="animate-fade-in">
          {activeTab === 'sent' && renderOrderList(historySent)}
          {activeTab === 'completed' && renderOrderList(historyCompleted)}
          {activeTab === 'cancelled' && renderOrderList(historyCancelled)}

          {/* Pagination Button */}
          {hasMore && placedOrders.length >= 20 && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={loadMoreOrders}
                disabled={loadingMore}
                className="px-10 py-4 bg-white border-2 border-red-600 text-red-600 font-extrabold uppercase tracking-widest rounded-2xl shadow-xl hover:bg-red-50 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100 flex items-center gap-3"
              >
                {loadingMore ? (
                  <>
                    <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                    Chargement...
                  </>
                ) : (
                  <>
                    <span>➕</span> Voir plus
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {ratingOrder && (
        <DriverRatingModal
          order={ratingOrder}
          onClose={() => setRatingOrder(null)}
          onSuccess={async () => {
            await updateOrderStatus(ratingOrder.id, 'Terminée');
            setRatingOrder(null);
            setActiveTab('completed');
          }}
        />
      )}

      {trackingOrder && (() => {
        const resto = restaurants.find((r: any) => r.id === trackingOrder.restaurantId);
        
        console.log(`[DEBUG_ORDER_SCREEN] Opening Tracking Modal for Order: ${trackingOrder.id}`);
        console.log(`[DEBUG_ORDER_SCREEN] -> Restaurant ID: ${trackingOrder.restaurantId}`);
        console.log(`[DEBUG_ORDER_SCREEN] -> Restaurant Found: ${resto ? "YES" : "NO"}`);
        console.log(`[DEBUG_ORDER_SCREEN] -> Restaurant Location:`, resto?.location);
        console.log(`[DEBUG_ORDER_SCREEN] -> Customer Location:`, trackingOrder.customerLocation);

        return (
          <LiveTrackingModal
            isOpen={!!trackingOrder}
            onClose={() => setTrackingOrder(null)}
            orderId={trackingOrder.id}
            clientLocation={trackingOrder.customerLocation}
            restaurantLocation={resto?.location}
            livreurId={trackingOrder.livreurId}
            restaurantName={resto?.name || trackingOrder.restaurantName}
          />
        );
      })()}
    </div>
  );
};

export default OrderScreen;